import { styled, alpha } from '@mui/material/styles';
import { Drawer, Box, Typography, TypographyProps, IconButton, Button } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/** Ancho fijo del panel en sm+ (misma idea que ExchangeDrawer: paper estático). */
export const LABEL_PRINT_DRAWER_WIDTH_PX = 720;

/**
 * Paper del Drawer: ancho + colores del tema activo (claro u oscuro).
 */
export const labelPrintDrawerPaperSx: SxProps<Theme> = {
  width: { xs: '100%', sm: `${LABEL_PRINT_DRAWER_WIDTH_PX}px` },
  minWidth: { xs: 0, sm: `${LABEL_PRINT_DRAWER_WIDTH_PX}px` },
  maxWidth: { xs: '100%', sm: `${LABEL_PRINT_DRAWER_WIDTH_PX}px` },
  height: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  bgcolor: 'background.paper',
  color: 'text.primary',
};

export const StyledDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': labelPrintDrawerPaperSx,
}));

export const DrawerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

export const DrawerHeader = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  boxSizing: 'border-box',
  minWidth: 0,
}));

export const DrawerTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '1.125rem',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  margin: 0,
  padding: 0,
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ProgressContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxSizing: 'border-box',
  minWidth: 0,
}));

export const ProgressBar = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.12)
      : theme.palette.grey[200],
  borderRadius: theme.spacing(0.5),
  height: 8,
  overflow: 'hidden',
  boxSizing: 'border-box',
}));

export const ProgressBarFill = styled(Box)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(0.5),
  background: `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.success.main})`,
  transition: 'width 0.35s ease-in-out',
}));

export const ProgressInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  fontSize: '14px',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
  minWidth: 0,
  overflowWrap: 'anywhere',
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.paper,
}));

export const DrawerFooter = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  minWidth: 0,
}));

export const FooterButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

export const PreviousButton = styled(Button)(({ theme }) => ({
  minWidth: '120px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.hover,
  border: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
}));

export const NextButton = styled(Button)(({ theme }) => ({
  minWidth: '150px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  background: `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.success.main})`,
  color: theme.palette.common.white,
  '&:hover': {
    background: `linear-gradient(to right, ${theme.palette.info.dark}, ${theme.palette.success.dark})`,
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));

export const ConfirmButton = styled(Button)(({ theme }) => ({
  minWidth: '180px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  background: `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.success.main})`,
  color: theme.palette.common.white,
  '&:hover': {
    background: `linear-gradient(to right, ${theme.palette.info.dark}, ${theme.palette.success.dark})`,
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));
